// -- src/index.js

import "core-js/stable"; // Polyfills for ES features
import "regenerator-runtime/runtime"; // Polyfills for async/await and generators

import { ThemeProvider } from "@mui/material/styles";
import { Suspense, lazy, useLayoutEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import AppCrashBoundary from "./components/crash/AppCrashBoundary";
import { CrashBootMarker } from "./components/crash/AppCrashLifecycle";
import "./index.css";
import { SUPPORT_EMAIL, SUPPORT_PHONE_NUMBER } from "./constants";
import {
  addCrashBreadcrumb,
  initClientCrashReporter,
} from "./services/clientCrashReporter";
import { installDomMutationGuard } from "./services/domMutationGuard";
import { reportWebVitals } from "./reportWebVitals";
import { recordWebVitalMetric } from "./services/webVitalsReporter";
import theme from "./theme";
import { installGooglePlacesAutocompleteOverlayManager } from "./utils/googlePlacesAutocompleteOverlay";
import { isStandaloneLabEntry } from "./pages/lab/standaloneLabRoute";

const standaloneLab = isStandaloneLabEntry({
  environment: process.env.NODE_ENV,
  hostname: window.location.hostname,
  pathname: window.location.pathname,
});
const StandaloneLab = process.env.NODE_ENV === "development"
  ? lazy(() => import("./pages/lab/PuzzleLabPage"))
  : null;

if (!standaloneLab) {
  initClientCrashReporter({
  supportEmail: SUPPORT_EMAIL,
  supportPhone: SUPPORT_PHONE_NUMBER,
  });
  installDomMutationGuard({ addBreadcrumb: addCrashBreadcrumb });
  installGooglePlacesAutocompleteOverlayManager();
}

const AppBootMarker = () => {
  useLayoutEffect(() => {
    document.documentElement.removeAttribute(
      "data-linked-lives-dashboard-boot",
    );
  }, []);

  return null;
};

const app = standaloneLab ? (
  <ThemeProvider theme={theme}>
    <AppBootMarker />
    <Suspense fallback={null}><StandaloneLab /></Suspense>
  </ThemeProvider>
) : (
  <AppCrashBoundary>
    <CrashBootMarker />
    <ThemeProvider theme={theme}>
      <AppBootMarker />
      <App />
    </ThemeProvider>
  </AppCrashBoundary>
);

const rootElement = document.getElementById("root");

ReactDOM.createRoot(rootElement).render(app);

if (!standaloneLab) reportWebVitals(recordWebVitalMetric);
